import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { LocaleCode } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import { useBalanceSheetQuery, usePlausibilityQuery } from '@/api/queries';
import { PATH } from '@/router/PATH';
import {
    describeWindow,
    formatGeneratedAt,
    resolveLocale,
    toBalanceCheck,
    todayIso,
    toStatementRows,
    unwrapStatement,
    toWarningNotices,
    type WarningNotice,
    ledgerWindowFor,
    toUnmappedCodes,
    warningLineIdSet,
    type BalanceCheckView,
    type StatementColumns,
    type StatementRow,
} from '../statementMeta';
import { useStatementsRange } from '../statementsOutletContext';

export type { BalanceCheckView };

export type UseBalanceSheetPageResult = {
    locale: LocaleCode;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    /** Render-ready rows, or `null` until the first load resolves. */
    rows: StatementRow[] | null;
    /** Column headers, written from the server's own windows. */
    columns: StatementColumns;
    balance: BalanceCheckView | null;
    warningNotices: WarningNotice[];
    /**
     * Postable accounts the ledger's statement layout maps to no line, so their
     * balances appear nowhere on this statement. Server-reported; the statement
     * is incomplete until the layout covers them.
     */
    unmappedCodes: string[];
    onDismissWarnings: () => void;
    /** Second drill hop — open the journal filtered to a real account. */
    onViewLedger: (accountId: string) => void;
    /** `DD/MM/YYYY HH:mm` stamp shown in the statement footer. */
    generatedText: string;
};

/**
 * Drives the Balance Sheet page.
 *
 * Two requests per view, and the split is the API's rather than a choice: the
 * statement — whose comparative column, delta and windows all arrive in one
 * payload — and the plausibility advisories, which `StatementResponse` has no
 * field for.
 *
 * The advisory call is deliberately **not** allowed to fail the page. If it
 * errors, the statement still renders and the banner has nothing to show:
 * warnings must never look like a failure and must never block a statement.
 *
 * Nothing here sums, re-signs or recomputes a figure. Subtotals, totals, the
 * result for the period and every delta arrive computed.
 */
export const useBalanceSheetPage = (): UseBalanceSheetPageResult => {
    const { t, i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);
    const navigate = useNavigate();

    // The Statements shell owns the period, so this page has no date control of
    // its own. Only the range **end** is meaningful: a balance sheet states the
    // position *at* a date, cumulative from the start of the ledger, so the
    // range start has nothing to say to it. Today stands in when the page is
    // rendered outside the shell.
    const { toDate } = useStatementsRange();
    const asOf = toDate || todayIso();
    const [warningsDismissed, setWarningsDismissed] = React.useState(false);
    const generatedText = React.useRef(formatGeneratedAt(new Date())).current;

    // PRIOR_YEAR always: the comparative costs no extra request, and its column
    // is labelled from the window the server sends back with it.
    const query = useBalanceSheetQuery(asOf, locale, 'PRIOR_YEAR');
    const { data, error } = unwrapStatement(query.data, query.error);
    const plausibility = usePlausibilityQuery(asOf, locale);
    // Memoised because it feeds a `useMemo` below: a bare `?? []` mints a
    // fresh array on every render, so every dependent recomputes forever.
    const warnings = React.useMemo(
        () =>
            plausibility.data?.success
                ? plausibility.data.response.warnings
                : [],
        [plausibility.data],
    );

    // A fresh as-of is a fresh statement — un-dismiss its advisories.
    React.useEffect(() => {
        setWarningsDismissed(false);
    }, [asOf]);

    const rows = React.useMemo(() => {
        if (!data) {
            return null;
        }
        return toStatementRows(data.lines, warningLineIdSet(warnings));
    }, [data, warnings]);

    /**
     * "As of 30/07/2026", never "This period". A Balance Sheet is a position at a
     * date; the server states that date, and the comparative's date, so neither
     * header is guessed.
     */
    const columns = React.useMemo<StatementColumns>(() => {
        const label = (window: ReturnType<typeof describeWindow>) =>
            window?.at
                ? t('statements.columns.asOfDate', { date: window.at })
                : t('statements.columns.amount');
        const prior = describeWindow(data?.comparativeWindow ?? null);
        return {
            amount: label(describeWindow(data?.window ?? null)),
            prior: prior ? label(prior) : null,
            delta: prior ? t('statements.columns.deltaVsPrior') : null,
        };
    }, [data, t]);

    const balance = React.useMemo<BalanceCheckView | null>(
        () => (data ? toBalanceCheck(data.lines) : null),
        [data],
    );

    // Grouped, not concatenated: the server sends one advisory per unmapped
    // account and a chart past its layout produces dozens. Those are dropped
    // here and stated once, from the statement's own list, below.
    const notices = React.useMemo(() => toWarningNotices(warnings), [warnings]);
    const warningNotices = warningsDismissed ? [] : notices;

    // From the **statement**, not the advisories — it is scoped to this
    // statement and it survives a failed plausibility call.
    const unmappedCodes = React.useMemo(
        () => toUnmappedCodes(data?.unmappedAccounts ?? []),
        [data],
    );

    const onRetry = React.useCallback(() => {
        query.refetch();
        plausibility.refetch();
    }, [query, plausibility]);

    /**
     * The Journal **tab**, scoped to ledger start through the as-of date —
     * **not** to the shell's range. A Balance Sheet figure is a cumulative
     * balance, so the entries behind it run from the beginning of the ledger;
     * handing over the selected period would show a list that does not add up
     * to the figure the reader clicked. The trial balance drills the same way.
     */
    const onViewLedger = React.useCallback(
        (accountId: string) => {
            navigate(
                PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ACCOUNT(
                    accountId,
                    ledgerWindowFor(asOf),
                ),
            );
        },
        [navigate, asOf],
    );

    return {
        locale,
        isLoading: query.isLoading,
        error,
        onRetry,
        rows,
        columns,
        balance,
        warningNotices,
        // Not dismissible: an incomplete statement stays incomplete, and this
        // is the only thing on the page that says so.
        unmappedCodes,
        onDismissWarnings: () => setWarningsDismissed(true),
        onViewLedger,
        generatedText,
    };
};
